import type { TenantRole } from '@detect/shared';

/** A customer this user can manage through a provider organization (#16), as /auth/me reports it. */
export interface DelegatedTenantDto {
  tenantId: string;
  name: string;
  role: TenantRole;
  clientIds: string[] | null;
  viaTenantId: string;
}

export interface TenantSummaryLike {
  tenantId: string;
  name: string;
  status: string;
  role: TenantRole;
  clientIds: string[] | null;
  /** Set when the user reaches this tenant through a management grant, not a membership. */
  delegated?: { viaTenantId: string };
}

/**
 * The tenant list the SPA works with: the user's own memberships first (so the default tenant is
 * always their own), then the customers they manage. A real membership always wins over a delegation
 * for the same tenant. The server re-authorizes every request; this only shapes what the UI offers.
 */
export function mergeDelegatedTenants<T extends TenantSummaryLike>(
  own: readonly T[],
  delegated: readonly DelegatedTenantDto[] | undefined
): TenantSummaryLike[] {
  const have = new Set(own.map((t) => t.tenantId));
  const extra: TenantSummaryLike[] = [];
  for (const d of delegated ?? []) {
    if (have.has(d.tenantId)) continue;
    have.add(d.tenantId);
    extra.push({
      tenantId: d.tenantId,
      name: d.name,
      status: 'active',
      role: d.role,
      clientIds: d.clientIds,
      delegated: { viaTenantId: d.viaTenantId },
    });
  }
  return [...own, ...extra];
}
